/**
 * AI-like function to determine service icon based on service name
 * Uses keyword matching to intelligently select appropriate Bootstrap Icons
 * 
 * @param serviceName - The name of the service
 * @returns Bootstrap Icons class name (e.g., 'bi-scissors', 'bi-heart-pulse')
 */
export const getServiceIcon = (serviceName: string): string => {
  if (!serviceName) return 'bi-circle';
  
  const name = serviceName.toLowerCase();
  
  // ============================================
  // MEDICAL & HEALTHCARE (Specific first, then general)
  // ============================================
  
  // Emergency & Urgent Care
  if (name.match(/\b(emergency|urgent|ambulance|paramedic|first aid)\b/)) {
    return 'bi-ambulance';
  }
  
  // Cardiology
  if (name.match(/\b(cardiac|cardiology|heart|ecg|ekg|echocardiogram|stress test)\b/)) {
    return 'bi-heart-pulse';
  }
  
  // Dermatology
  if (name.match(/\b(dermatology|dermatologist|skin|acne|mole|rash|eczema|psoriasis|biopsy)\b/)) {
    return 'bi-droplet';
  }
  
  // Ophthalmology & Eye Care
  if (name.match(/\b(eye|vision|ophthalmology|optometry|optometrist|glasses|contact|retina|glaucoma)\b/)) {
    return 'bi-eye';
  }
  
  // Orthopedics
  if (name.match(/\b(orthopedic|orthopedist|bone|fracture|joint|knee|hip|shoulder|spine|sports medicine)\b/)) {
    return 'bi-activity';
  }
  
  // Mental Health & Psychology
  if (name.match(/\b(psychology|psychologist|psychiatry|psychiatrist|therapy|counseling|mental health|depression|anxiety|therapy session)\b/)) {
    return 'bi-heart';
  }
  
  // Chiropractic
  if (name.match(/\b(chiro|chiropractic|adjustment|alignment|back pain)\b/)) {
    return 'bi-activity';
  }
  
  // Physical Therapy & Rehabilitation
  if (name.match(/\b(physical therapy|pt|rehab|rehabilitation|occupational therapy|speech therapy|kinesiology)\b/)) {
    return 'bi-activity';
  }
  
  // Dental (Specific)
  if (name.match(/\b(dental|dentist|tooth|teeth|cleaning|checkup|root canal|extraction|implant|braces|orthodontist|oral surgery|gum|periodontist)\b/)) {
    return 'bi-tooth';
  }
  
  // Pediatrics
  if (name.match(/\b(pediatric|pediatrician|child|baby|infant|newborn|vaccination|immunization)\b/)) {
    return 'bi-heart-pulse';
  }
  
  // Gynecology & Women's Health
  if (name.match(/\b(gynecology|gynecologist|obgyn|obstetric|prenatal|pregnancy|mammogram|pap|women's health)\b/)) {
    return 'bi-heart-pulse';
  }
  
  // Urology
  if (name.match(/\b(urology|urologist|kidney|bladder|prostate)\b/)) {
    return 'bi-heart-pulse';
  }
  
  // Neurology
  if (name.match(/\b(neurology|neurologist|brain|headache|migraine|seizure|epilepsy|parkinson|alzheimer)\b/)) {
    return 'bi-brain';
  }
  
  // General Medical
  if (name.match(/\b(doctor|physician|medical|checkup|examination|consultation|appointment|clinic|hospital|primary care|family medicine|internal medicine)\b/)) {
    return 'bi-heart-pulse';
  }
  
  // ============================================
  // BEAUTY & PERSONAL CARE
  // ============================================
  
  // Hair Services
  if (name.match(/\b(hair|cut|trim|styl|color|dye|highlight|lowlight|balayage|ombre|perm|braid|weave|extension|wig|hair transplant)\b/)) {
    return 'bi-scissors';
  }
  
  // Nail Services
  if (name.match(/\b(manicure|pedicure|nail|polish|gel|acrylic|dip powder|nail art|nail extension)\b/)) {
    return 'bi-hand-thumbs-up';
  }
  
  // Skincare & Facials
  if (name.match(/\b(facial|skincare|skin care|microdermabrasion|chemical peel|facial treatment|acne treatment|anti-aging)\b/)) {
    return 'bi-flower1';
  }
  
  // Massage & Spa
  if (name.match(/\b(massage|spa|treatment|therapy|relax|aromatherapy|hot stone|swedish|deep tissue|sports massage|reflexology)\b/)) {
    return 'bi-flower1';
  }
  
  // Hair Removal
  if (name.match(/\b(wax|waxing|hair removal|laser|laser hair|electrolysis|threading|sugaring)\b/)) {
    return 'bi-lightning';
  }
  
  // Makeup & Cosmetics
  if (name.match(/\b(makeup|make-up|cosmetic|mascara|lipstick|foundation|makeup artist|bridal makeup|airbrush)\b/)) {
    return 'bi-palette';
  }
  
  // Eyelash & Eyebrow Services
  if (name.match(/\b(eyelash|lash|extension|lift|tint|eyebrow|brow|microblading|threading|lamination)\b/)) {
    return 'bi-eye';
  }
  
  // Tattoo & Piercing
  if (name.match(/\b(tattoo|tattooing|piercing|body art|ink)\b/)) {
    return 'bi-pencil';
  }
  
  // Tanning
  if (name.match(/\b(tan|tanning|spray tan|sunbed)\b/)) {
    return 'bi-sun';
  }
  
  // ============================================
  // FITNESS & WELLNESS
  // ============================================
  
  // Personal Training
  if (name.match(/\b(personal trainer|personal training|pt session|coach|fitness coach)\b/)) {
    return 'bi-trophy';
  }
  
  // Yoga & Pilates
  if (name.match(/\b(yoga|pilates|meditation|mindfulness|stretching)\b/)) {
    return 'bi-heart';
  }
  
  // Fitness Classes
  if (name.match(/\b(fitness|gym|workout|exercise|cardio|strength|weight|crossfit|zumba|spin|cycling|aerobics|bootcamp)\b/)) {
    return 'bi-heart';
  }
  
  // Nutrition & Diet
  if (name.match(/\b(nutrition|nutritionist|diet|dietitian|meal plan|weight loss|weight management)\b/)) {
    return 'bi-cup-hot';
  }
  
  // ============================================
  // AUTOMOTIVE SERVICES
  // ============================================
  
  // Car Repair & Maintenance
  if (name.match(/\b(car|auto|vehicle|automotive|oil change|tire|brake|repair|maintenance|tune-up|inspection|smog|diagnostic)\b/)) {
    return 'bi-car-front';
  }
  
  // Car Wash & Detailing
  if (name.match(/\b(car wash|detailing|wax|polish|interior|exterior)\b/)) {
    return 'bi-car-front';
  }
  
  // Towing
  if (name.match(/\b(tow|towing|tow truck)\b/)) {
    return 'bi-truck';
  }
  
  // ============================================
  // HOME SERVICES & REPAIR
  // ============================================
  
  // Plumbing
  if (name.match(/\b(plumb|plumber|pipe|water|leak|faucet|sink|toilet|drain|sewer|water heater|boiler)\b/)) {
    return 'bi-droplet';
  }
  
  // Electrical
  if (name.match(/\b(electric|electrician|wiring|outlet|light|fixture|install|panel|circuit|breaker)\b/)) {
    return 'bi-lightning-charge';
  }
  
  // HVAC (Heating, Ventilation, Air Conditioning)
  if (name.match(/\b(hvac|heating|cooling|ac|air conditioning|furnace|boiler|duct|ventilation|air quality)\b/)) {
    return 'bi-thermometer-half';
  }
  
  // Painting
  if (name.match(/\b(paint|painting|painter|wall|interior|exterior|stain|refinish)\b/)) {
    return 'bi-brush';
  }
  
  // Cleaning Services
  if (name.match(/\b(clean|cleaning|house|home|maid|service|janitor|deep clean|move-in|move-out|carpet|window)\b/)) {
    return 'bi-broom';
  }
  
  // Landscaping & Lawn Care
  if (name.match(/\b(landscape|landscaping|lawn|mow|mowing|grass|yard|garden|tree|hedge|mulch|fertilizer|sprinkler)\b/)) {
    return 'bi-flower1';
  }
  
  // Roofing
  if (name.match(/\b(roof|roofing|roofer|shingle|gutter|siding)\b/)) {
    return 'bi-house';
  }
  
  // Flooring
  if (name.match(/\b(floor|flooring|carpet|hardwood|tile|laminate|install|refinish)\b/)) {
    return 'bi-square';
  }
  
  // Handyman Services
  if (name.match(/\b(handyman|handy|fix|repair|maintenance|install|assembly|mount|hang)\b/)) {
    return 'bi-tools';
  }
  
  // Locksmith
  if (name.match(/\b(lock|lockmith|key|unlock|rekey|safe)\b/)) {
    return 'bi-lock';
  }
  
  // Pest Control
  if (name.match(/\b(pest|exterminator|bug|insect|rodent|termite|fumigation)\b/)) {
    return 'bi-bug';
  }
  
  // ============================================
  // TECHNOLOGY & IT SERVICES
  // ============================================
  
  // Computer Repair
  if (name.match(/\b(computer|laptop|pc|mac|repair|tech|software|install|virus|malware|data recovery)\b/)) {
    return 'bi-laptop';
  }
  
  // Phone & Mobile Device Repair
  if (name.match(/\b(phone|mobile|smartphone|iphone|android|device|repair|screen|battery|unlock)\b/)) {
    return 'bi-phone';
  }
  
  // Network & Internet
  if (name.match(/\b(internet|wifi|network|setup|install|router|modem|ethernet|fiber|dsl|cable)\b/)) {
    return 'bi-wifi';
  }
  
  // IT Support & Consulting
  if (name.match(/\b(it support|tech support|help desk|troubleshoot|system admin|network admin)\b/)) {
    return 'bi-gear';
  }
  
  // Web Development & Design
  if (name.match(/\b(web|website|development|design|programming|coding|app development|seo)\b/)) {
    return 'bi-code-slash';
  }
  
  // ============================================
  // LEGAL & PROFESSIONAL SERVICES
  // ============================================
  
  // Legal Services
  if (name.match(/\b(legal|lawyer|attorney|law|litigation|court|divorce|criminal|civil|contract|estate|will|trust)\b/)) {
    return 'bi-briefcase';
  }
  
  // Accounting & Tax
  if (name.match(/\b(accounting|accountant|tax|bookkeeping|financial|audit|cpa|tax preparation|tax filing)\b/)) {
    return 'bi-calculator';
  }
  
  // Financial Planning & Investment
  if (name.match(/\b(financial planning|financial advisor|investment|wealth management|retirement|portfolio)\b/)) {
    return 'bi-graph-up';
  }
  
  // Real Estate
  if (name.match(/\b(real estate|realtor|realty|property|home|house|buy|sell|rent|lease|appraisal|inspection)\b/)) {
    return 'bi-house';
  }
  
  // Insurance
  if (name.match(/\b(insurance|insurer|policy|claim|coverage|life insurance|health insurance|auto insurance)\b/)) {
    return 'bi-shield-check';
  }
  
  // Consulting
  if (name.match(/\b(consulting|consultant|advisory|strategy|business consulting|management consulting)\b/)) {
    return 'bi-briefcase';
  }
  
  // ============================================
  // EDUCATION & TUTORING
  // ============================================
  
  // Tutoring & Academic
  if (name.match(/\b(tutor|tutoring|lesson|class|course|training|teach|learn|academic|homework|test prep|sat|act)\b/)) {
    return 'bi-book';
  }
  
  // Music Lessons
  if (name.match(/\b(music|piano|guitar|violin|drum|voice|singing|lesson|instructor)\b/)) {
    return 'bi-music-note-beamed';
  }
  
  // Language Learning
  if (name.match(/\b(language|esl|english|spanish|french|german|chinese|japanese|translation|interpreter)\b/)) {
    return 'bi-translate';
  }
  
  // Driving School
  if (name.match(/\b(driving|driver|license|road test|behind the wheel)\b/)) {
    return 'bi-car-front';
  }
  
  // ============================================
  // FOOD & CATERING
  // ============================================
  
  // Catering & Food Service
  if (name.match(/\b(cater|catering|food|meal|dinner|lunch|breakfast|delivery|event|party|buffet)\b/)) {
    return 'bi-cup-hot';
  }
  
  // Cooking Classes
  if (name.match(/\b(cooking|culinary|chef|recipe|baking|pastry)\b/)) {
    return 'bi-cup-hot';
  }
  
  // ============================================
  // PHOTOGRAPHY & VIDEO
  // ============================================
  
  // Photography
  if (name.match(/\b(photo|photography|photographer|shoot|session|portrait|wedding|event|family|headshot|commercial)\b/)) {
    return 'bi-camera';
  }
  
  // Videography
  if (name.match(/\b(video|videography|videographer|film|filming|cinematography|editing)\b/)) {
    return 'bi-camera-video';
  }
  
  // ============================================
  // PET SERVICES
  // ============================================
  
  // Pet Grooming
  if (name.match(/\b(pet groom|dog groom|cat groom|bath|nail trim|deshed)\b/)) {
    return 'bi-scissors';
  }
  
  // Veterinary
  if (name.match(/\b(vet|veterinary|veterinarian|animal|pet|dog|cat|checkup|vaccination|surgery)\b/)) {
    return 'bi-heart';
  }
  
  // Pet Sitting & Walking
  if (name.match(/\b(pet sit|dog walk|cat sit|pet care|boarding|daycare)\b/)) {
    return 'bi-heart';
  }
  
  // Pet Training
  if (name.match(/\b(pet train|dog train|obedience|behavior|puppy)\b/)) {
    return 'bi-trophy';
  }
  
  // ============================================
  // EVENT & ENTERTAINMENT SERVICES
  // ============================================
  
  // Event Planning
  if (name.match(/\b(event planning|event planner|wedding planning|party planning|coordination)\b/)) {
    return 'bi-calendar-event';
  }
  
  // DJ & Music
  if (name.match(/\b(dj|disc jockey|music|entertainment|sound|audio|karaoke)\b/)) {
    return 'bi-music-note-beamed';
  }
  
  // Party Services
  if (name.match(/\b(party|celebration|birthday|anniversary|wedding|reception)\b/)) {
    return 'bi-balloon';
  }
  
  // ============================================
  // TRAVEL & TRANSPORTATION
  // ============================================
  
  // Travel Planning
  if (name.match(/\b(travel|trip|vacation|itinerary|booking|travel agent)\b/)) {
    return 'bi-airplane';
  }
  
  // Transportation
  if (name.match(/\b(transport|taxi|uber|lyft|ride|shuttle|limo|limousine)\b/)) {
    return 'bi-car-front';
  }
  
  // ============================================
  // PERSONAL SERVICES
  // ============================================
  
  // Personal Shopping
  if (name.match(/\b(personal shopping|shopper|stylist|wardrobe)\b/)) {
    return 'bi-bag';
  }
  
  // Organizing & Decluttering
  if (name.match(/\b(organize|organizing|declutter|decluttering|minimalist|home organization)\b/)) {
    return 'bi-box';
  }
  
  // Laundry & Dry Cleaning
  if (name.match(/\b(laundry|dry clean|wash|press|alteration|tailor)\b/)) {
    return 'bi-basket';
  }
  
  // ============================================
  // BUSINESS SERVICES
  // ============================================
  
  // Printing & Copying
  if (name.match(/\b(print|printing|copy|copying|fax|scan|document)\b/)) {
    return 'bi-printer';
  }
  
  // Notary
  if (name.match(/\b(notary|notarize|notarization)\b/)) {
    return 'bi-file-earmark-text';
  }
  
  // Translation & Interpretation
  if (name.match(/\b(translate|translation|interpreter|interpretation)\b/)) {
    return 'bi-translate';
  }
  
  // ============================================
  // DEFAULT GENERIC ICONS
  // ============================================
  
  // Consultation & Meetings
  if (name.match(/\b(consult|meeting|session|appointment|conference)\b/)) {
    return 'bi-calendar-check';
  }
  
  // Installation & Setup
  if (name.match(/\b(install|setup|configuration|assembly)\b/)) {
    return 'bi-gear';
  }
  
  // Repair & Maintenance (generic)
  if (name.match(/\b(repair|fix|maintenance|service)\b/)) {
    return 'bi-tools';
  }
  
  // Default fallback
  return 'bi-circle';
};

/** Matches customer booking step 1: active, non-soft-deleted services only. */
export const isCustomerBookableService = (service: any): boolean => {
  if (!service) return false;
  const isActive = service.isActive ?? service.is_active;
  const deleted = service.deleted;
  return isActive !== false && deleted !== true;
};

export const filterCustomerBookableServices = <T,>(services: T[]): T[] =>
  services.filter(isCustomerBookableService);
